import { Card, CardContent, Grid, Stack, Typography } from "@mui/material";
import StatCard from "../../components/StatCard";
import { StatSummary } from "../../components/StatSummary";
import useUserDashboardSummary from "../../hooks/use-user-dashboard-summary";
import ChartCard from "../../components/chart/chart-card";
import DoughnutChart from "../../components/chart/doughnut-chart";
import SingleBarChart from "../../components/chart/single-bar-chart";
import { CURRENCY } from "../../shared/constants";
import { selectLoggedInUser } from "../auth/auth.slice";
import { useAppSelector } from "../../app/hooks";
import { PermissionCode } from "../../shared/enums";
import theme from "../../app/app-theme";
import { getColorForMembershipStatus } from "../members/components/member-summary";
import ProfileImage from "../members/components/profile-image";
import thumbsUpImage from "../../resources/images/emoji/thumbs-up.png";
import { AttachMoneyIcon, AttendanceIcon, FingerprintIcon, PeopleIcon, PersonOffIcon, SmsIcon, TodayIcon } from "../../shared/icons";
import StyledChip from "../../components/styled-chip";
import { getPermissionsForLoggedInUser } from "../../shared/utils";

const UserDashboard = () => {
    const summary = useUserDashboardSummary();
    const loggedInUser = useAppSelector(selectLoggedInUser);
    const permissions = getPermissionsForLoggedInUser(loggedInUser);

    return (
        <>
            {summary &&
                <>
                    <StatSummary>
                        <StatCard title="Total Members" value={summary?.totalMembers} icon={PeopleIcon} />
                        <StatCard title="Inactive Members" value={summary?.inactiveMembers} icon={PersonOffIcon} />
                        {/* <StatCard title="Active Members" value={summary?.activeMembers} icon={MembersIcon} /> */}
                        <StatCard title="Today Attendance" value={summary?.todayAttendance} icon={AttendanceIcon} />
                        {permissions.includes(PermissionCode.INCOME_VIEW) &&
                            <>
                                <StatCard title={`Income - This Month (${CURRENCY})`} value={summary?.memberPaymentsThisMonth} icon={AttachMoneyIcon} />
                                <StatCard title='Next Billing Date' value={loggedInUser?.roleData.businessUser?.business?.nextBillingDate} icon={TodayIcon} />
                                <StatCard title={`Due (${CURRENCY})`} value={loggedInUser?.roleData.businessUser?.business?.dueAmount} icon={AttachMoneyIcon} textColor={(loggedInUser?.roleData.businessUser?.business?.dueAmount && loggedInUser?.roleData.businessUser?.business?.dueAmount > 0) ? theme.palette.error.main : theme.palette.success.main} />
                            </>
                        }
                        {permissions.includes(PermissionCode.MESSAGES_VIEW) &&
                            <StatCard title='Free SMS Balance' value={(loggedInUser?.roleData.businessUser?.business.plan.messageLimit ?? 0) - (loggedInUser?.roleData.businessUser?.business.messageCount ?? 0)} icon={SmsIcon} />
                        }
                        {permissions.includes(PermissionCode.BIOMETRIC_DEVICE_VIEW) &&
                            <StatCard title='Failed Biometric Transactions' value={summary?.failedBiometricTransactions} icon={FingerprintIcon} textColor={`${summary?.failedBiometricTransactions && summary?.failedBiometricTransactions > 0 ? theme.palette.error.main : theme.palette.success.main}`} />
                        }
                    </StatSummary>
                    <Grid container direction="row" justifyContent="flex-start" >
                        <Grid size={{ lg: 5, md: 6, sm: 12, xs: 12 }} >
                            <ChartCard><SingleBarChart data={summary.dailyAttendanceTrend.map(item => { return { name: item.key, value: item.value } })} label='Daily Attendance' height={204} xAxisInterval={7} /></ChartCard>
                        </Grid>
                        {permissions.includes(PermissionCode.INCOME_VIEW) &&
                            <Grid size={{ lg: 4, md: 6, sm: 12, xs: 12 }} >
                                <ChartCard><SingleBarChart data={summary.monthlyPaymentTrend.map(item => { return { name: item.key, value: item.value } })} label='Monthly Income' height={204} xAxisAngle={-90} /></ChartCard>
                            </Grid>
                        }
                        <Grid size={{ lg: 3, md: 12, sm: 12, xs: 12 }}>
                            <Card style={{ marginTop: "12px", padding: 12, backgroundColor: theme.palette.primary.contrastText, height: "290px", overflowY: "auto" }}>
                                <h3 style={{ justifySelf: "center" }}>
                                    Expired Members {summary?.expiredMembers.length > 0 ? `(${summary?.expiredMembers.length})` : "(0)"}
                                </h3>
                                <>
                                    {summary?.expiredMembers?.map(member =>
                                        <Stack spacing={2} sx={{ flexGrow: 1, m: 2 }} key={member.memberId}>
                                            <Grid container justifyContent="start" alignItems="center" spacing={2} sx={{ padding: 1, borderRadius: 1, elevation: 1, border: `1px solid ${theme.palette.primary.main}` }}>
                                                <ProfileImage profileImageKey={member.profileImageKey} />
                                                <Typography style={{ marginLeft: "4px", fontWeight: "500" }}>{member.name}</Typography>
                                            </Grid>
                                        </Stack>
                                    )}
                                    {summary?.expiredMembers.length === 0 &&
                                        <>
                                            <Typography style={{ textAlign: "center", fontStyle: "italic" }}>No expired members</Typography>
                                            <img src={thumbsUpImage} style={{ maxWidth: "100%" }} alt="" />
                                        </>
                                    }
                                </>
                            </Card>
                        </Grid>
                        <Grid size={{ lg: 3, md: 6, sm: 12, xs: 12 }}>
                            <ChartCard><DoughnutChart data={summary.membershipStatusCount?.map(item => { return { name: item.key, value: item.value, color: getColorForMembershipStatus(item.key) } })} label='Membership Status' /></ChartCard>
                        </Grid>
                        <Grid size={{ lg: 6, md: 6, sm: 12, xs: 12 }}>
                            <ChartCard><SingleBarChart data={summary.dailyMessageTrend?.map(item => { return { name: item.key, value: item.value } })} label='Daily Messages' height={204} xAxisInterval={7} /></ChartCard>
                        </Grid>
                        <Grid size={{ lg: 3, md: 12, sm: 12, xs: 12 }}>
                            <Card style={{ marginTop: "12px", padding: 12, backgroundColor: theme.palette.primary.contrastText, height: "290px", overflowY: "auto" }}>
                                <h3 style={{ justifySelf: "center" }}>
                                    Expired Notes {summary?.expiredNotes.length > 0 ? `(${summary?.expiredNotes.length})` : "(0)"}
                                </h3>
                                <>
                                    {summary?.expiredNotes?.map(note =>
                                        <Stack spacing={2} sx={{ flexGrow: 1, m: 2 }} key={note.noteId}>
                                            <Card sx={{ width: "100%" }} style={{ border: `1px solid ${theme.palette.primary.main}` }}>
                                                <CardContent style={{ padding: "8px" }}>
                                                    <Typography gutterBottom sx={{ color: 'text.secondary', fontSize: 14, m: 0 }}>
                                                        {note.targetName}
                                                        <span><StyledChip label={note.targetType} size="small" sx={{ float: "right" }} /></span>
                                                    </Typography>

                                                    <Typography variant="body1" component="div">
                                                        {note.content}
                                                    </Typography>
                                                </CardContent>
                                            </Card>
                                        </Stack>
                                    )}
                                    {summary?.expiredNotes.length === 0 &&
                                        <>
                                            <Typography style={{ textAlign: "center", fontStyle: "italic" }}>No expired notes</Typography>
                                            <img src={thumbsUpImage} style={{ maxWidth: "100%" }} alt="" />
                                        </>
                                    }
                                </>
                            </Card>
                        </Grid>
                    </Grid>
                </>
            }
        </>
    )
}

export default UserDashboard;